import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { transitOrdersAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowUpRight,
  ArrowDownLeft,
  Trash2,
  Package,
  Calendar,
  Filter,
} from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";

const TransitHistory = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadOrders();
  }, [filter]);

  const loadOrders = async () => {
    try {
      const orderType = filter === "all" ? null : filter;
      const res = await transitOrdersAPI.getAll(orderType);
      setOrders(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento degli ordini");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await transitOrdersAPI.delete(deleteId);
      toast.success("Ordine eliminato");
      setDeleteId(null);
      loadOrders();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const getOrderTypeInfo = (type) => {
    if (type === "in") {
      return {
        label: "Entrata",
        icon: ArrowDownLeft,
        color: "bg-emerald-50 text-emerald-600 border-emerald-200",
        iconColor: "text-emerald-500",
      };
    }
    return {
      label: "Uscita",
      icon: ArrowUpRight,
      color: "bg-red-50 text-red-600 border-red-200",
      iconColor: "text-red-500",
    };
  };

  return (
    <div data-testid="transit-history-page" className="animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/transito")}
            className="p-2.5 text-gray-500 hover:text-[#C9A227] bg-white rounded-xl shadow-sm hover:shadow transition-all"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <div>
            <h1 className="font-serif text-3xl text-gray-900">Storico Ordini</h1>
            <p className="text-gray-500 text-sm mt-1">
              Tutti gli ordini in entrata e uscita
            </p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[160px] bg-white border-gray-200 rounded-xl">
              <SelectValue placeholder="Tutti" />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200 rounded-xl">
              <SelectItem value="all">Tutti gli ordini</SelectItem>
              <SelectItem value="in">Solo Entrate</SelectItem>
              <SelectItem value="out">Solo Uscite</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="text-center py-20 text-gray-500">Caricamento...</div>
      ) : orders.length === 0 ? (
        <div className="text-center py-20">
          <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 text-lg font-medium">Nessun ordine trovato</p>
          <p className="text-gray-400 text-sm mt-1">Gli ordini appariranno qui una volta creati</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => {
            const typeInfo = getOrderTypeInfo(order.order_type);
            const TypeIcon = typeInfo.icon;

            return (
              <div
                key={order.id}
                className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all"
                data-testid={`order-${order.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${order.order_type === 'in' ? 'bg-emerald-50' : 'bg-red-50'}`}>
                      <TypeIcon className={`w-5 h-5 ${typeInfo.iconColor}`} strokeWidth={2} />
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <Badge className={`${typeInfo.color} border font-semibold`}>
                          {typeInfo.label}
                        </Badge>
                        <span className="text-gray-400 text-sm font-medium">
                          {order.items.length} profumi
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-gray-500 text-sm">
                        <Calendar className="w-4 h-4" />
                        {format(new Date(order.created_at), "dd MMMM yyyy, HH:mm", { locale: it })}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">
                        {order.total_liters.toFixed(1)} L
                      </p>
                      <p className="text-xs text-gray-400">Totale</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteId(order.id)}
                      className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Items preview */}
                <div className="border-t border-gray-100 pt-4 mt-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {order.items.slice(0, 5).map((item, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-50 rounded-lg p-3"
                      >
                        <p className="text-[#C9A227] text-xs font-semibold">{item.perfume_code}</p>
                        <p className="text-gray-700 text-sm font-bold truncate">{item.perfume_name}</p>
                        <p className="text-gray-500 text-xs mt-1">{item.quantity_liters} L</p>
                      </div>
                    ))}
                    {order.items.length > 5 && (
                      <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-center">
                        <p className="text-gray-500 text-sm font-medium">
                          +{order.items.length - 5} altri
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {order.notes && (
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <p className="text-amber-700 text-sm">
                      <span className="font-semibold">Note:</span> {order.notes}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white border-gray-200 rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-900 font-serif">Conferma Eliminazione</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              Sei sicuro di voler eliminare questo ordine dallo storico? Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-100 border-0 text-gray-600 hover:bg-gray-200 rounded-xl">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 text-white hover:bg-red-600 rounded-xl"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TransitHistory;
